import "./seed.css";
import React, { useState, useMemo, useEffect } from "react";
import {
  Container,
  Title,
  Table,
  Loader,
  Overlay,
  Group,
  Button,
  Modal,
  TextInput,
  Textarea,
  Select,
  Badge,
  ActionIcon,
  Pagination,
  Stack,
  Center,
  Text,
} from "@mantine/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSeedling, faTrash } from "@fortawesome/free-solid-svg-icons";
import useMagicItems from "../../../../../hooks/corral/magicItem";
import useConstellation from "../../../../../hooks/corral/constellation";
import { useParams, useNavigate } from "react-router";
import { useCurrentUser } from "../../../../../hooks/currentUser";

const tierColor = {
  common: "gray",
  uncommon: "green",
  rare: "blue",
  epic: "violet",
  wonderous: "cyan",
  vanguard: "teal",
  legendary: "orange",
  mythic: "red",
  ascendant: "yellow",
  godlike: "pink",
  terminal: "indigo",
  foundational: "dark",
};

const glowStyle = {
  boxShadow: "0 0 8px 2px rgba(255,255,255,0.8)",
  animation: "pulse 2s infinite",
};

const Seed = () => {
  const { treeId, quadrantId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useCurrentUser();

  const {
    items,
    loading: itemLoading,
    modalOpen,
    tierOptions,
    glyphOptions,
    formValues,
    handleInputChange,
    handleSelectChange,
    addItem,
    disenchantItem,
    openModal,
    closeModal,
  } = useMagicItems();

  const { loading: constellationLoading, saveGlyphSlot } = useConstellation({
    currentUser,
    treeId,
    quadrantId,
  });

  const loading = itemLoading || constellationLoading;

  // filter only high-tier items
  const usableItems = items.filter((it) => parseInt(it.tier) > 199);

  // pagination setup
  const [page, setPage] = useState(1);
  const perPage = 10;
  const totalPages = Math.ceil(usableItems.length / perPage);

  // clamp page if items shrink
  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages || 1);
    }
  }, [totalPages, page]);

  // slice for current page
  const pagedItems = useMemo(
    () => usableItems.slice((page - 1) * perPage, page * perPage),
    [usableItems, page],
  );

  const handleSave = async ({ item }) => {
    await disenchantItem(item.inventoryId);
    await saveGlyphSlot({ glyphId: item.glyphId });
    navigate(`/campaigns/corral/arboretum/${treeId}`);
  };

  const rows = pagedItems.map((it) => {
    const tier = tierOptions.find((t) => t.value === `${it.tier}`);
    const tierKey = tier?.label?.toLowerCase();
    const isGlowing = it.tier >= 200;
    return (
      <Table.Tr key={it.inventoryId}>
        <Table.Td>{it.name}</Table.Td>
        <Table.Td>
          <Badge
            color={tierColor[tierKey]}
            style={isGlowing ? glowStyle : undefined}
          >
            {tier.label}
          </Badge>
        </Table.Td>
        <Table.Td>
          {it.glyph && (
            <Badge
              autoContrast
              color={it.glyph.backgroundColor}
              style={{ outline: "1px solid grey" }}
            >
              {it.glyph.displayName}
            </Badge>
          )}
        </Table.Td>
        <Table.Td>
          <ActionIcon
            color="yellow"
            variant="light"
            onClick={() => handleSave({ item: it })}
          >
            <FontAwesomeIcon icon={faSeedling} />
          </ActionIcon>
        </Table.Td>
      </Table.Tr>
    );
  });

  return (
    <Container>
      <Group justify="space-between" mb="md">
        <Title order={2}>Seed Placement</Title>
        {!loading && <Button onClick={openModal}>Add Item</Button>}
      </Group>

      {loading && (
        <Overlay blur={1}>
          <Loader />
        </Overlay>
      )}

      {!loading && (
        <Stack spacing="xs">
          {usableItems.length === 0 ? (
            <Text>No high-tier items available.</Text>
          ) : (
            <>
              <Table striped highlightOnHover withColumnBorders>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Name</Table.Th>
                    <Table.Th>Rarity</Table.Th>
                    <Table.Th>Foundational Affinity</Table.Th>
                    <Table.Th>Plant Seed</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>{rows}</Table.Tbody>
              </Table>

              {totalPages > 1 && (
                <Center>
                  <Pagination
                    page={page}
                    onChange={setPage}
                    total={totalPages}
                    siblings={1}
                    boundaries={1}
                    size="sm"
                  />
                </Center>
              )}
            </>
          )}
        </Stack>
      )}

      <Modal
        opened={modalOpen}
        onClose={closeModal}
        title="Add Magic Item"
        size="xl"
        centered
      >
        <TextInput
          name="name"
          label="Name"
          placeholder="Excalibur"
          mb="md"
          required
          value={formValues.name}
          onChange={handleInputChange}
        />
        <TextInput
          name="crafter"
          label="Crafter"
          placeholder="Arthur Wolfe"
          mb="md"
          required
          value={formValues.crafter}
          onChange={handleInputChange}
        />
        <Select
          label="Magic Tier"
          placeholder="Select tier"
          data={tierOptions}
          mb="md"
          searchable
          required
          value={formValues.tier}
          onChange={handleSelectChange("tier")}
        />
        <Select
          label="Glyph Origin"
          placeholder="Choose glyph"
          data={glyphOptions}
          mb="md"
          searchable
          required
          value={formValues.glyphId}
          onChange={handleSelectChange("glyphId")}
        />
        <Textarea
          name="description"
          label="Description"
          placeholder="Item details…"
          autosize
          minRows={3}
          maxRows={15}
          mb="md"
          required
          value={formValues.description}
          onChange={handleInputChange}
        />
        <Group position="right">
          <Button
            onClick={addItem}
            disabled={
              !formValues.name || !formValues.tier || !formValues.glyphId
            }
          >
            Save
          </Button>
        </Group>
      </Modal>
    </Container>
  );
};

export default Seed;
